import type { Recipe } from './contracts';
import { PALETTES } from './materials';

function object(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${label} must be an object`);
  return value as Record<string, unknown>;
}
function keys(value: Record<string, unknown>, expected: readonly string[], label: string): void {
  for (const key of Object.keys(value)) if (!expected.includes(key)) throw new Error(`${label}: unknown field ${key}`);
  for (const key of expected) if (!Object.prototype.hasOwnProperty.call(value, key)) throw new Error(`${label}: missing ${key}`);
}
function range(value: unknown, lo: number, hi: number, label: string, integer = false): void {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < lo || value > hi || (integer && !Number.isInteger(value))) {
    throw new Error(`${label}: expected ${integer ? 'integer ' : ''}${lo}..${hi}`);
  }
}
function oneOf(value: unknown, allowed: readonly string[], label: string): void {
  if (typeof value !== 'string' || !allowed.includes(value)) throw new Error(`${label}: expected ${allowed.join('|')}`);
}
export function validateRecipe(value: unknown): Recipe {
  const r = object(value, 'recipe');
  keys(r, ['schema', 'id', 'assetId', 'generatorId', 'template', 'seed', 'paletteId', 'detail', 'params'], 'recipe');
  if (r.schema !== 'helix.route-prop-recipe.v1') throw new Error('Unsupported recipe schema');
  for (const key of ['id', 'assetId', 'template']) {
    if (typeof r[key] !== 'string' || !/^[a-z][a-z0-9_.-]{2,100}$/.test(r[key] as string)) throw new Error(`Invalid ${key}`);
  }
  range(r.seed, 0, 0xffffffff, 'seed', true);
  oneOf(r.paletteId, Object.keys(PALETTES), 'paletteId');
  oneOf(r.detail, ['readable', 'standard'], 'detail');
  const p = object(r.params, 'params');
  const dims = (minW: number, maxW: number, minH: number, maxH: number, minAspect: number, maxAspect: number): void => {
    range(p.width, minW, maxW, 'width'); range(p.height, minH, maxH, 'height');
    range(Number(p.width) / Number(p.height), minAspect, maxAspect, 'width/height');
  };
  switch (r.generatorId) {
  case 'route.fallen_log.v1':
    keys(p, ['width', 'height', 'profile', 'mossCover', 'saltWear'], 'params');
    dims(110, 320, 34, 100, 2.7, 3.5); oneOf(p.profile, ['whole', 'split', 'low'], 'profile');
    range(p.mossCover, 0, 0.7, 'mossCover'); range(p.saltWear, 0, 0.5, 'saltWear'); break;
  case 'route.dead_bracken.v1':
    keys(p, ['width', 'height', 'lean', 'fronds', 'brokenTips'], 'params');
    dims(50, 110, 65, 130, 0.65, 1); range(p.lean, -0.25, 0.25, 'lean');
    range(p.fronds, 5, 9, 'fronds', true); range(p.brokenTips, 0, 3, 'brokenTips', true); break;
  case 'route.tide_pool.v1':
    keys(p, ['width', 'height', 'edge', 'ripples', 'depth'], 'params');
    dims(360, 700, 55, 95, 5.8, 8.5); oneOf(p.edge, ['silt', 'broken-stone', 'shingle'], 'edge');
    range(p.ripples, 2, 7, 'ripples', true); range(p.depth, 0.15, 0.85, 'depth'); break;
  case 'route.mist_shelf.v1':
    keys(p, ['width', 'height', 'shelf', 'banks', 'density'], 'params');
    dims(240, 360, 75, 115, 2.7, 3.5); oneOf(p.shelf, ['continuous', 'broken'], 'shelf');
    range(p.banks, 2, 4, 'banks', true); range(p.density, 0.15, 0.7, 'density'); break;
  default: throw new Error(`Unknown generatorId: ${String(r.generatorId)}`);
  }
  // The strict grammar has primitive root fields and one flat primitive params object.
  // Copy both levels so validated output never aliases caller-owned authoring data.
  return { ...r, params: { ...p } } as unknown as Recipe;
}
